/**
 * Machine load for the capacity board.
 *
 * The engine has no scheduling endpoint, so load is the routing itself: every
 * operation on an open work order books its estimated setup + run hours against
 * the machine it names. Operations with no machine are surfaced as unassigned
 * rather than spread across machines, because guessing where they will run is
 * exactly the thing the board exists to expose.
 */
import type { WorkOrder } from "./erpApi";
import type { Machine, WorkOrderOperation } from "./shopfloorApi";

/** Work orders in these states no longer consume capacity. */
const DONE = new Set(["completed", "done", "cancelled", "closed"]);

export type OperationBooking = {
  workOrder: WorkOrder;
  operation: WorkOrderOperation;
  hours: number;
};

export type MachineLoad = {
  machineKey: string;
  label: string;
  /** Hours booked against this machine in the window. */
  booked: number;
  /** Hours the machine can take in the window; null when capacity is unset. */
  capacity: number | null;
  /** booked / capacity, uncapped so an overbooked machine reads above 1. */
  load: number | null;
  bookings: OperationBooking[];
};

export function operationHours(op: WorkOrderOperation): number {
  return (op.est_setup_hrs || 0) + (op.est_run_hrs || 0);
}

export function isOpen(wo: WorkOrder): boolean {
  return !DONE.has((wo.status || "").toLowerCase());
}

/**
 * Group bookings by machine over `workingDays` of the machine's daily capacity.
 * `operationsByWo` maps a work order id to the operations fetched for it.
 */
export function machineLoads(
  machines: Machine[],
  workOrders: WorkOrder[],
  operationsByWo: Map<string, WorkOrderOperation[]>,
  opts: { workingDays?: number } = {},
): MachineLoad[] {
  const workingDays = opts.workingDays ?? 5;

  const byKey = new Map<string, MachineLoad>();
  const ensure = (key: string, label: string, capacity: number | null) => {
    let row = byKey.get(key);
    if (!row) {
      row = { machineKey: key, label, booked: 0, capacity, load: null, bookings: [] };
      byKey.set(key, row);
    }
    return row;
  };

  for (const m of machines) {
    if (m.status !== "active") continue;
    const perDay = m.capacity_hours_per_day;
    ensure(
      m.id || m.code,
      m.name ? `${m.code} · ${m.name}` : m.code,
      typeof perDay === "number" && perDay > 0 ? perDay * workingDays : null,
    );
  }

  const unassigned = ensure("__unassigned", "Unassigned", null);

  for (const wo of workOrders) {
    if (!isOpen(wo)) continue;
    const ops = operationsByWo.get(wo.id ?? "") ?? [];
    for (const op of ops) {
      const hours = operationHours(op);
      const key = op.machine_id || op.machine_code || null;
      const row = key ? byKey.get(key) : null;
      const target =
        row ??
        (key
          ? // The routing names a machine the machine list does not have.
            ensure(key, op.machine_name || op.machine_code || key, null)
          : unassigned);
      target.booked += hours;
      target.bookings.push({ workOrder: wo, operation: op, hours });
    }
  }

  // Drop the unassigned bucket when nothing landed in it.
  if (unassigned.bookings.length === 0) byKey.delete("__unassigned");

  return [...byKey.values()]
    .map((row) => ({
      ...row,
      load: row.capacity && row.capacity > 0 ? row.booked / row.capacity : null,
    }))
    .sort((a, b) => (b.load ?? -1) - (a.load ?? -1) || b.booked - a.booked);
}

export type CapacityTotals = {
  booked: number;
  capacity: number | null;
  /** Machines booked above their capacity. */
  overbooked: number;
  unassignedHours: number;
};

export function capacityTotals(loads: MachineLoad[]): CapacityTotals {
  let booked = 0;
  let capacity = 0;
  let hasCapacity = false;
  let overbooked = 0;
  let unassignedHours = 0;

  for (const row of loads) {
    booked += row.booked;
    if (row.capacity != null) {
      capacity += row.capacity;
      hasCapacity = true;
    }
    if (row.load != null && row.load > 1) overbooked += 1;
    if (row.machineKey === "__unassigned") unassignedHours += row.booked;
  }

  return {
    booked,
    capacity: hasCapacity ? capacity : null,
    overbooked,
    unassignedHours,
  };
}
